import { baseUrl, httpClient } from 'src/pages/admin/providers/AppDataProvider';
import { getAuth } from 'src/pages/admin/providers/AppAuthProvider';

import type {
  ApiResponse,
  CreateLeaderboardPeriodPayload,
  LeaderboardDefinitionId,
  LeaderboardPeriod,
  ReplaceLeaderboardTiersPayload,
  UpdateLeaderboardPeriodPayload,
} from './types';
import {
  normalizeDefinition,
  normalizeDefinitions,
  normalizePeriods,
  normalizeSettlement,
  normalizeTierList,
} from './utils';

import {
  MoneyRequest,
  MoneyRequestPage,
  moneyCall,
} from 'src/pages/admin/services/moneyRequestApi';

const resourceBase = 'world-cup/leaderboard-config';

function unwrap<T>(json: ApiResponse<T> | T): T {
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as ApiResponse<T>).data;
  }

  return json as T;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { json } = await httpClient(`${baseUrl}/${resourceBase}${path}`, options as any);

  return unwrap<T>(json);
}

export const leaderboardConfigApi = {
  async getDefinitions(signal?: AbortSignal) {
    const data = await request<unknown>('/definitions', {
      method: 'GET',
      signal,
    } as any);

    return normalizeDefinitions(data);
  },

  async getDefinition(id: LeaderboardDefinitionId, signal?: AbortSignal) {
    const data = await request<unknown>(`/definitions/${id}`, {
      method: 'GET',
      signal,
    } as any);

    return normalizeDefinition(data, id);
  },

  async getTiers(periodId: string, id: LeaderboardDefinitionId, signal?: AbortSignal) {
    const data = await request<unknown>(`/periods/${periodId}/definitions/${id}/tiers`, {
      method: 'GET',
      signal,
    } as any);

    return normalizeTierList(data, id);
  },

  async replaceTiers(
    periodId: string,
    id: LeaderboardDefinitionId,
    payload: ReplaceLeaderboardTiersPayload
  ) {
    const data = await request<unknown>(`/periods/${periodId}/definitions/${id}/tiers`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    return normalizeTierList(data, id);
  },

  async copyTiersFrom(periodId: string, id: LeaderboardDefinitionId, fromPeriodId: string) {
    const data = await request<unknown>(
      `/periods/${periodId}/definitions/${id}/tiers/copy-from/${fromPeriodId}`,
      {
        method: 'POST',
      }
    );

    return normalizeTierList(data, id);
  },

  async applyTiersToAll(periodId: string, id: LeaderboardDefinitionId) {
    return request<unknown>(`/periods/${periodId}/definitions/${id}/tiers/apply-all`, {
      method: 'POST',
    });
  },

  async listPeriods(signal?: AbortSignal): Promise<LeaderboardPeriod[]> {
    const data = await request<unknown>('/periods', {
      method: 'GET',
      signal,
    } as any);

    return normalizePeriods(data);
  },

  createPeriod(payload: CreateLeaderboardPeriodPayload) {
    return request<LeaderboardPeriod>('/periods', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updatePeriod(periodId: string, payload: UpdateLeaderboardPeriodPayload) {
    return request<LeaderboardPeriod>(`/periods/${periodId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  deletePeriod(periodId: string) {
    return request<unknown>(`/periods/${periodId}`, {
      method: 'DELETE',
    });
  },

  async getSettlement(
    periodId: string,
    id: LeaderboardDefinitionId,
    signal?: AbortSignal
  ) {
    const data = await request<unknown>(
      `/periods/${periodId}/definitions/${id}/settlement`,
      {
        method: 'GET',
        signal,
      } as any
    );

    return normalizeSettlement(data, periodId, id);
  },

  payAllSettlement(periodId: string, id: LeaderboardDefinitionId) {
    return request<unknown>(
      `/periods/${periodId}/definitions/${id}/settlement/pay-all`,
      {
        method: 'POST',
      }
    );
  },

  payEntry(entryId: string) {
    return request<unknown>(`/settlement/entries/${entryId}/pay`, {
      method: 'POST',
    });
  },

  retryEntry(entryId: string) {
    return request<unknown>(`/settlement/entries/${entryId}/retry`, {
      method: 'POST',
    });
  },

  // ── Amount override: request -> approval by a different admin (XBIT-4530) ──
  // The former PATCH .../settlement/entries/:entryId/amount is gone; paying stays a separate action.

  requestAmountOverride(entryId: string, body: { amountUsdc: string; reason?: string }) {
    return moneyCall<MoneyRequest>(
      'POST',
      `${resourceBase}/settlement/entries/${entryId}/amount-requests`,
      body
    );
  },

  listAmountRequests(params?: { page?: number; size?: number; status?: string }) {
    return moneyCall<MoneyRequestPage>(
      'GET',
      `${resourceBase}/settlement/amount-requests`,
      undefined,
      params
    );
  },

  approveAmountRequest(id: string, reason?: string) {
    return moneyCall<MoneyRequest>(
      'POST',
      `${resourceBase}/settlement/amount-requests/${id}/approve`,
      reason ? { reason } : {}
    );
  },

  rejectAmountRequest(id: string, reason: string) {
    return moneyCall<MoneyRequest>(
      'POST',
      `${resourceBase}/settlement/amount-requests/${id}/reject`,
      { reason }
    );
  },

  cancelAmountRequest(id: string, reason?: string) {
    return moneyCall<MoneyRequest>(
      'POST',
      `${resourceBase}/settlement/amount-requests/${id}/cancel`,
      reason ? { reason } : {}
    );
  },

  async exportRanking(periodId: string): Promise<void> {
    const { accessToken } = (await getAuth()) ?? {};
    const search = new URLSearchParams({ periodId }).toString();
    const url = `${baseUrl}/${resourceBase}/export?${search}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

    if (!res.ok) {
      throw new Error(`Export failed: ${res.status} ${res.statusText}`);
    }

    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const filename = /filename="(.+?)"/.exec(cd)?.[1] ?? `ranking-${periodId}.xlsx`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  },
};
